<template>
  <form
    id="listing-form-gtm"
    class="lister-branding"
    :class="{ initializing: !initialized }"
    @submit.prevent="submitOrUpdateListing"
  >

    <div
      class="tabbed accordion formcordion no-margin-top"
      style="margin-bottom: 80px"
      :class="{ 'no-margin-top': isEditing }"
    >
      <a
        href="https://help.aperfectspace.com/en/collections/6847033-for-listers"
        target="_blank"
        class="float-right mt-3 help"
      >
        Need Help?
      </a>
      <div class="loader-wrapper" v-if="!initialized">
        <loader :loading="!initialized"></loader>
      </div>
      <div class="content-panes" v-if="initialized">
        <div
          class="tab-content"
          :class="{ active: activeSection === 'auth' }"
          data-section="auth"
        >
          <div class="content has-fixed-submit">
            <inline-auth-panel @authed="isNowAuthed"></inline-auth-panel>
          </div>
        </div>
        <!-- // STEP 1 // -->
        <div
          class="tab-content"
          :class="{ active: activeSection === 1 }"
          :data-section="1"
        >
          <div class="content has-fixed-submit">
            <h3 class="keyline font-lg lh-tight">STEP 1 - Add your address</h3>

            <p class="padded pl-0 font-base">
              Start typing your address and if it does not appear you can enter it manually.
              If at any time you need help, please click on the 'Need Help?' button.
            </p>

            <div class="form-section columned address">
              <div class="form-group vanilla address-full no-top-margin">
                <label>Your property address</label>
                <auto-complete-address
                  @placechanged="fillAddress"
                  placeholder="Start typing your address..."
                  :disabled="isEditing"
                  :key="this.listing.sub_premises_number + this.listing.street_number + this.listing.street_address + this.listing.suburb + this.listing.state + this.listing.postal_code"
                ></auto-complete-address>
                <div class="manual-address-btn" @click="manualAddress = true" v-show="!showManualAddressFields">Can't find your address? Click here to enter manually</div>
                <input-error v-if="errors.full_address">{{
                  errors.full_address[0]
                }}</input-error>
              </div>

              <div class="form-group vanilla address-sub-premises" v-show="showManualAddressFields">
                <label for="listing-property-sub-premises-number"
                  >Unit/Flat</label
                >
                <input
                  id="listing-property-sub-premises-number"
                  type="text"
                  name="sub_premises_number"
                  v-model="listing.sub_premises_number"
                  :disabled="isEditing"
                />
              </div>

              <div class="form-group vanilla address-street-number" v-show="showManualAddressFields">
                <label for="listing-property-address-street-number">
                  Street number <span class="required">*</span>
                </label>
                <input
                  id="listing-property-address-street-number"
                  type="text"
                  name="street_number"
                  v-model="listing.street_number"
                  @keydown="delete errors.street_number"
                  required
                  :disabled="isEditing"
                />
                <input-error v-if="errors.street_number">{{
                  errors.street_number[0]
                }}</input-error>
              </div>

              <div class="form-group vanilla address-street-address" v-show="showManualAddressFields">
                <label for="listing-property-street-address">
                  Street address
                  <span class="required">*</span>
                </label>
                <input
                  id="listing-property-street-address"
                  type="text"
                  name="address"
                  v-model="listing.street_address"
                  @keydown="delete errors.street_address"
                  :disabled="isEditing"
                />
                <input-error v-if="errors.street_address">{{
                  errors.street_address[0]
                }}</input-error>
              </div>

              <div class="form-group vanilla address-suburb" v-show="showManualAddressFields">
                <label for="listing-property-suburb">
                  Suburb
                  <span class="required">*</span>
                </label>
                <input
                  id="listing-property-suburb"
                  type="text"
                  name="suburb"
                  v-model="listing.suburb"
                  @keydown="delete errors.suburb"
                  required
                  :disabled="isEditing"
                />
                <input-error v-if="errors.suburb">{{
                  errors.suburb[0]
                }}</input-error>
              </div>

              <div class="form-group vanilla address-state" v-show="showManualAddressFields">
                <label for="listing-property-state">
                  State
                  <span class="required">*</span>
                </label>
                <select-wrapper
                  id="listing-property-state"
                  name="state"
                  :options="stateOptions"
                  v-model="listing.state"
                  @input="delete errors.state"
                  :disabled="isEditing"
                  popperClass="secondary-style"
                ></select-wrapper>
                <input-error v-if="errors.state">{{
                  errors.state[0]
                }}</input-error>
              </div>

              <div class="form-group vanilla address-postcode" v-show="showManualAddressFields">
                <label for="listing-property-postcode">
                  Postcode
                  <span class="required">*</span>
                </label>
                <input
                  id="listing-property-postcode"
                  type="text"
                  name="postcode"
                  v-model="listing.postal_code"
                  @keydown="delete errors.postal_code"
                  required
                  :disabled="isEditing"
                />
                <input-error v-if="errors.postal_code">{{
                  errors.postal_code[0]
                }}</input-error>
              </div>

              <div class="form-group vanilla address-full" v-show="showManualAddressFields">
                <label for="listing-property-country">
                  Country
                  <span class="required">*</span>
                </label>
                <select-wrapper
                  id="listing-property-country"
                  name="country"
                  :options="countryOptions"
                  v-model="listing.country"
                  @input="delete errors.country"
                  :disabled="isEditing"
                  popperClass="secondary-style"
                ></select-wrapper>
                <input-error v-if="errors.country">{{
                  errors.country[0]
                }}</input-error>
              </div>
            </div>
          </div>
        </div>
        <!-- // STEP 1 // -->

        <!-- // STEP 2 // -->
        <div
          class="tab-content"
          :class="{ active: activeSection === 2 }"
          :data-section="2"
        >
          <div class="content has-fixed-submit">
            <h3 class="keyline font-lg lh-tight">
              STEP 2 - Add your property details
            </h3>

            <p class="padded pl-0 font-base">
              Please add your property type, a great description,
              upload your image gallery and add your price. Remember if you need
              any help just click on our Help Centre Icon top right.
            </p>

            <div class="intro lh-tight mb-3">
              <p>
                <span class="label-like wt-sb">
                  Property Type
                  <span class="required">*</span>
                </span>
              </p>
            </div>
            <div class="form-group vanilla">
              <div class="radio-group">
                <div class="column">
                  <radio
                    name="location_type"
                    label="Private"
                    input-value="Private"
                    v-model="listing.location_type"
                    class="secondary-style"
                  ></radio>
                </div>
                <div class="column">
                  <radio
                    name="location_type"
                    label="Commercial"
                    input-value="Commercial"
                    v-model="listing.location_type"
                    class="secondary-style"
                  ></radio>
                </div>
              </div>
              <select-wrapper
                id="listing-property-type"
                name="property_type"
                :options="allowedPropertyTypes"
                v-model="listing.property_type"
                @input="delete errors.property_type"
                popperClass="secondary-style"
              ></select-wrapper>
              <input-error v-if="errors.property_type">{{
                errors.property_type[0]
              }}</input-error>
            </div>

            <div class="intro lh-tight">
              <p>
                <span class="label-like wt-sb">Configuration and Spaces</span>
                <span v-floating-tooltip="'Please select all areas and spaces that describe your space.'"
                  >
                  <img :src="'/images/icons/tooltip.svg'" class="tooltip" />
                </span>
              </p>
            </div>
            <div class="increment-group columns-4">
              <template v-for="option in allowedSpaceTypes">
                <div class="column" :key="option.id" v-if="option.is_countable">
                  <label class="font-base">{{ option.label }}</label>
                  <div class="form-group-meta-count float-right mr-4">
                    <button
                      type="button"
                      class="circle-badge small"
                      @click="decrement(option.id)"
                    >
                      －
                    </button>
                    <button
                      type="button"
                      class="circle-badge small"
                      @click="increment(option.id)"
                    >
                      ＋
                    </button>
                    <input
                      type="number"
                      pattern="^[-/d]/d*$"
                      onkeydown="if(event.key==='.'){event.preventDefault();}"
                      oninput="event.target.value = event.target.value.replace(/[^0-9]*/g,'');"
                      v-model="listing.meta_counts.space_types[option.id]"
                    />
                  </div>
                </div>
                <div class="column" :key="option.id" v-else>
                  <label class="font-base" :for="'space_type_' + option.id">{{
                    option.label
                  }}</label>
                  <div class="form-group-meta-count sm float-right mr-6">
                    <input
                      :id="'space_type_' + option.id"
                      style="height: 18px; margin-right: -10px; margin-top: 2px"
                      type="checkbox"
                      :input-value="option.id"
                      :value="option.id"
                      v-model="listing.space_types"
                      @input="delete errors.space_types"
                      class="secondary-checkbox"
                    />
                    <label :for="'space_type_' + option.id"></label>
                  </div>
                </div>
              </template>
            </div>

            <div class="intro lh-tight mt-6">
              <p>
                <span class="label-like wt-sb">
                  Property Description
                  <span class="required">*</span>
                  <a
                    target="_blank"
                    href="https://help.aperfectspace.com/en/articles/8578205-how-do-i-write-a-great-description-for-my-listing"
                    v-floating-tooltip="
                        'Click for a guide to help you craft a great description'
                    "
                    >
                    <img :src="'/images/icons/tooltip.svg'" class="tooltip" />
                  </a>
                </span>
              </p>
            </div>

            <div class="form-group vanilla">
              <p class="font-sm wt-lt mb-4">100 words minimum. Information about all available spaces, design elements and unique features required. See above info button for more details.</p>
              <word-count-textarea
                v-model="listing.description"
                @input="delete errors.description"
                size="slim"
                :auto-adjust-height="true"
                :disabled="isEditing"
                style="width:100%"
              ></word-count-textarea>
              <input-error v-if="errors.description">{{
                errors.description[0]
              }}</input-error>
            </div>

            <div class="intro lh-tight mt-8" v-if="!isEditing">
              <p>
                <span class="label-like wt-sb">
                  Add your images here
                  <a
                    target="_blank"
                    href="https://help.aperfectspace.com/en/articles/8610095-what-type-of-photo-files-do-i-use-in-my-image-gallery"
                    v-floating-tooltip="
                        'Please click here for a guide and FAQ’s on how to upload your images.'
                    "
                    >
                    <img :src="'/images/icons/tooltip.svg'" class="tooltip" />
                  </a>
                </span>
              </p>
              <p class="font-sm wt-lt">Image specs: JPEG, PNG and WebP format, File Size: <10MB, iphone/smartphone images in HEIC format cannot be uploaded. Landscape orientation preferred. See above info button for more details.</p>
              <media-uploader
                class="mt-4 media-upload-secondary-style"
                name="gallery"
                :multiple="true"
                :accept="['image/jpeg', 'image/png', 'image/webp']"
                :max-file-size="10240"
                :max-items="maxImages"
                :initial-value="listing.gallery"
                v-model="listing.gallery"
                @input="delete errors.gallery"
                :key="uuid"
                @isReadyToSubmit="isReadyToSubmitHandler"
              ></media-uploader>
              <input-error v-if="errors.gallery">{{
                errors.gallery[0]
              }}</input-error>
            </div>

            <div class="intro lh-tight mt-8" v-if="isEditing">
              <p>
                <span class="label-like wt-sb">Extra Images</span>
                <a
                  target="_blank"
                  href="https://help.aperfectspace.com/en/articles/8610095-what-type-of-photo-files-do-i-use-in-my-image-gallery"
                  v-floating-tooltip="
                    'Please ensure you submit at step 4 to add the extra images to your listing for review'
                  "
                >
                  <img :src="'/images/icons/tooltip.svg'" class="tooltip" />
                </a>
              </p>
              <media-uploader
                class="mt-4 media-upload-secondary-style"
                name="extra_images"
                :multiple="true"
                :accept="['image/*']"
                :max-file-size="10240"
                :max-items="maxImages"
                :initial-value="listing.extra_images"
                v-model="listing.extra_images"
                @input="delete errors.extra_images"
                :key="uuid"
                @isReadyToSubmit="isReadyToSubmitHandler"
              ></media-uploader>
              <input-error v-if="errors.extra_images">{{
                errors.extra_images[0]
              }}</input-error>
            </div>
          </div>
        </div>
        <!-- // STEP 2 // -->

        <!-- // STEP 3 // -->
        <div
          class="tab-content"
          :class="{ active: activeSection === 3 }"
          :data-section="3"
        >
          <div class="content has-fixed-submit">
            <h3 class="keyline font-lg lh-tight">STEP 3 - Add usage & logistics here.</h3>

            <p class="padded pl-0 font-base">
              Please select which productions you are happy to have at your space?
            </p>

            <div class="intro lh-tight mb-2">
              <p>
                <span class="label-like wt-sb">
                  What types of hire is your location suitable for?
                </span>
                <span
                  href="#"
                  v-floating-tooltip="
                    'Select all hire types that apply to your property'
                  "
                >
                  <img :src="'/images/icons/tooltip.svg'" class="tooltip" />
                </span>
              </p>
            </div>
            <div class="form-group vanilla pb-0">
              <input-error v-if="errors.availability_types">{{
                errors.availability_types[0]
              }}</input-error>
              <div v-for="option in meta.availability_types">
                <checkbox-pill
                  :label="option.label"
                  :input-value="option.id"
                  v-model="listing.availability_types"
                  @input="delete errors.availability_types"
                  class="secondary-style"
                ></checkbox-pill>
              </div>
            </div>

            <div class="group columns-2">
              <div class="column" style="padding-right: 15px">
                <div class="intro lh-tight mt-2 mb-2">
                  <p>
                    <span class="label-like wt-sb"
                      >What is the capacity of your property?
                    </span>
                    <span v-floating-tooltip="
                      'The number of cast/crew/guests you want to limit at your property during a production'
                    ">
                      <img :src="'/images/icons/tooltip.svg'" class="tooltip" />
                    </span>
                  </p>
                </div>
                <template>
                  <select-wrapper
                    id="listing-capacity"
                    name="capacity"
                    :options="capacityOptions"
                    v-model="listing.capacity"
                    @input="delete errors.capacity"
                    popperClass="secondary-style"
                  ></select-wrapper>
                </template>
              </div>
              <div class="column" style="padding-right: 15px">
                <div class="intro lh-tight mt-2 mb-2">
                  <p>
                    <span class="label-like wt-sb"
                      >Areas Available
                    </span>
                  </p>
                </div>
                <template>
                  <select-wrapper
                    id="listing-areas-available"
                    name="areas_available"
                    :options="areasAvialableOptions"
                    v-model="listing.areas_available"
                    @input="delete errors.areas_available"
                    popperClass="secondary-style"
                  ></select-wrapper>
                </template>
              </div>
            </div>

            <div class="intro lh-tight mt-4 mb-2">
              <p>
                <span class="label-like wt-sb">Logistics</span>
                <span v-floating-tooltip="
                  'Please check all the logistics that apply to your space'
                ">
                  <img :src="'/images/icons/tooltip.svg'" class="tooltip" />
                </span>
              </p>
            </div>
            <div class="increment-group columns-3">
              <template v-for="option in allowedLogistics">
                <div class="column" :key="option.id">
                  <checkbox
                    :label="option.label"
                    :input-value="option.id"
                    v-model="listing.logistics"
                    @input="delete errors.logistics"
                    class="secondary-style"
                  ></checkbox>
                </div>
              </template>
            </div>

            <div class="intro lh-tight mt-6 mb-4">
              <p>
                <span class="label-like wt-sb">Daily Hire Rate</span>
                <span
                  href="#"
                  v-floating-tooltip="
                    'Enter your day rate minimum or request a pricing review from our team'
                  "
                >
                  <img :src="'/images/icons/tooltip.svg'" class="tooltip" />
                </span>
              </p>
            </div>
            <div
              class="form-group vanilla"
              :class="{ 'has-input-error': errors.day_rate }"
            >
              <div class="input-group">
                <div class="prefix">$</div>
                <input
                  id="listing-day-rate"
                  type="number"
                  name="day_rate"
                  min="1800"
                  step="1"
                  v-model="listing.day_rate"
                  @keyup="
                    listing.day_rate <= 0
                      ? (listing.day_rate = '')
                      : listing.day_rate
                  "
                  @focus="delete errors.day_rate"
                />
              </div>
              <input-error v-if="errors.day_rate">{{
                errors.day_rate[0]
              }}</input-error>
              <p class="mt-4 mb-4 font-base">
                Please note we have a minimum day rate of $1800. Prices are
                informed by many factors including size, scale and geography. If
                you already have a price in mind, please indicate it here and
                our team will review as part of our listing review process. If
                you are unsure of a suitable day rate for your location, please
                request a price review from our team below.
              </p>
              <div class="column">
                <checkbox
                  v-model="listing.appraisal_requested"
                  @input="delete errors.appraisal_requested"
                  :is-boolean="true"
                  label="Please provide me with a pricing review based on my listing submission"
                  class="secondary-style"
                >
                </checkbox>
                <input-error v-if="errors.appraisal_requested">{{
                  errors.appraisal_requested[0]
                }}</input-error>
              </div>
            </div>

            <div class="detail-section">
              <checkbox
                v-model="listing.agree_terms"
                @input="delete errors.agree_terms"
                :is-boolean="true"
                :disabled="isEditing"
                class="secondary-style"
              >
                <template v-slot:checkbox-label>
                  I agree to the
                  <a
                    href="/terms-and-conditions/lister"
                    @click.stop.prevent="showListerTermsModal"
                    class="secondary-style"
                  >
                    lister terms &amp; conditions
                  </a>
                  <span class="required">*</span>
                </template>
              </checkbox>
              <input-error v-if="errors.agree_terms">{{
                errors.agree_terms[0]
              }}</input-error>
            </div>
          </div>
        </div>
        <!-- // STEP 3 // -->

        <!-- // STEP 4 // -->
        <div
          class="tab-content"
          :class="{ active: activeSection === 4 }"
          :data-section="4"
        >
          <div class="content has-fixed-submit">
            <h3 class="keyline font-lg lh-tight">
              STEP 4 - You're almost there
            </h3>

            <p class="padded pl-0 font-base">
              Now all we need is for you to review your listing and click submit.<br/>
              If you want to make any edits, its simple, just click edit and make your changes or
              move back to the step you wish to edit. <br/>
              Remember if you need any help just reach out to one of our team.
            </p>

            <div class="group columns-2">
              <div class="column" style="padding-right: 15px">
                <h4 class="keyline font-sm lh-bold mb-2">
                  Property Information
                  <a
                    href="#"
                    class="edit-button fg-near-black"
                    @click="openSection({ id: 2 }, true)"
                  >
                    <img :src="'/images/icons/edit-2.svg'" />
                    <label>Edit</label>
                  </a>
                </h4>
                <div
                  id="property-images"
                  class="image-gallery"
                  v-if="Object.keys(listing.gallery).length"
                >
                  <div class="inner">
                    <ul
                      class="featherlight-gallery-container"
                      v-featherlight-gallery
                    >
                      <li v-for="media in listing.gallery">
                        <div class="inner">
                          <a
                            :href="
                              media.original_url
                                ? media.original_url
                                : media.preview_url
                            "
                            class="interactive tinted tinted-hover tinted-20 tinted-hover-only"
                          >
                            <img
                              :src="
                                media.large_url
                                  ? media.large_url
                                  : media.preview_url
                              "
                              :alt="media.name"
                              class="cover"
                            />
                          </a>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="alert warning font-sm" v-if="Object.keys(listing.gallery).length === 0 && noImagesWarning">
                  <p>
                    Oops you have not added any images. You have two options<br/>
                    1) Please revisit Step 2 and add up to 20 images<br/>
                    2) <a href="/contact" target="_blank">Contact us</a> to arrange a photoshoot or you can arrange your own shoot, and submit
                    without images for now. <br/>
                    Note: we cannot publish without professional high end images.
                  </p>
                </div>
                <div class="bg-ultralight-grey mt-6">
                  <p class="p-2 font-base">
                    <label class="fg-secondary">
                      {{ listing.suburb }}, {{ listing.state }}, Australia
                    </label>
                    <br /><br />
                    <label class="font-md">
                      {{ propertyTypeLabel }}
                    </label>
                  </p>
                </div>

                <h4 class="keyline font-sm lh-bold mb-2 mt-8">
                  Description
                  <a
                    href="#"
                    class="edit-button fg-near-black"
                    @click="openSection({ id: 2 }, true)"
                    v-if="!isEditing"
                  >
                    <img :src="'/images/icons/edit-2.svg'" />
                    <label>Edit</label>
                  </a>
                </h4>
                <span class="truncated-description font-base">
                  {{ listing.description }}
                </span>

                <template v-if="Object.keys(listing.extra_images).length">
                  <h4 class="keyline font-sm lh-bold mb-2 mt-8">
                    Extra images
                    <a
                      href="#"
                      class="edit-button fg-near-black"
                      @click="openSection({ id: 2 }, true)"
                    >
                      <img :src="'/images/icons/edit-2.svg'" />
                      <label>Edit</label>
                    </a>
                  </h4>
                  <span class="truncated-description">
                    Images added for review and approval
                  </span>
                </template>
              </div>

              <div class="column" style="padding-left: 15px">
                <h4 class="keyline font-sm lh-bold mb-2">
                  Configuration
                  <a
                    href="#"
                    class="edit-button fg-near-black"
                    @click="openSection({ id: 2 }, true)"
                  >
                    <img :src="'/images/icons/edit-2.svg'" />
                    <label>Edit</label>
                  </a>
                </h4>
                <div class="group columns-2">
                  <template v-for="option in allowedSpaceTypes">
                    <template v-if="listing.space_types.includes(option.id)">
                      <div
                        class="column font-base"
                        style="padding: 5px"
                        v-if="listing.meta_counts.space_types[option.id]"
                      >
                        <div class="circle-badge active secondary-style">
                          {{ listing.meta_counts.space_types[option.id] }}
                        </div>
                        {{ option.label }}
                      </div>
                      <div class="column font-base" style="padding: 5px" v-else>
                        <div
                          class="circle-badge active secondary-style"
                          style="font-size: 12px"
                        >
                          Yes
                        </div>
                        {{ option.label }}
                      </div>
                    </template>
                  </template>
                </div>

                <h4 class="keyline font-sm lh-bold mb-2 mt-8">
                  Logistics
                  <a
                    href="#"
                    class="edit-button fg-near-black"
                    @click="openSection({ id: 3 }, true)"
                  >
                    <img :src="'/images/icons/edit-2.svg'" />
                    <label>Edit</label>
                  </a>
                </h4>
                <template v-for="option in allowedLogistics">
                  <div
                    class="mb-3 w-50 font-base"
                    v-if="listing.logistics.includes(option.id)"
                  >
                    {{ option.label }}
                  </div>
                </template>
                <p v-if="!listing.logistics.length" class="font-base">
                  You have not selected any option
                </p>

                <div v-show="!this.$store.getters['user/account'].phone">
                  <h4 class="keyline font-sm lh-bold mb-2 mt-8">
                    Phone number
                  </h4>
                  <div class="form-group vanilla address-street-number">
                    <input
                      id="listing-property-phone-number"
                      type="text"
                      v-model="listing.phone"
                      @keydown="delete errors.phone"
                    />
                    <span class="font-sm mt-1">Please provide a phone number so we can contact you about your listing</span>
                    <input-error v-if="errors.phone">{{
                      errors.phone[0]
                    }}</input-error>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- // STEP 4 // -->
      </div>
    </div>
    <div
      class="form-group submit no-divider align-center mt-8 footer"
      v-if="initialized"
    >
      <div>
        <div class="w-25 float-left">
          <button
            v-if="activeSection > 1"
            type="button"
            class="button primary"
            @click="openSection({ id: activeSection - 1 }, true, isEditing || activeSection < maxReachedSection)"
            :disabled="loading"
          >
            Back
          </button>
          <div v-else>&nbsp;</div>
        </div>
        <div class="w-50 float-left mt-1">
          <a
            v-for="n in 4"
            :key="n"
            class="circle-badge spaced secondary-style"
            :class="{
              active: n === activeSection,
              hoverable: n <= maxReachedSection || isEditing,
            }"
            @click="
              n <= maxReachedSection || isEditing
                ? openSection({ id: n }, true, isEditing || activeSection < maxReachedSection)
                : null
            "
          >
            {{ n }}
          </a>
        </div>
        <div class="w-25 float-right">
          <loader-button
            v-if="!isEditing"
            type="button"
            class="button clear"
            label="Clear all"
            @click.native="clear"
          ></loader-button>
          <button
            v-if="activeSection < 4"
            type="button"
            class="button primary"
            :class="!isEditing && maxReachedSection <= activeSection ? `listing-step-${activeSection}-next-btn-gtm` : ''"
            @click="
              openSection({ id: parseInt(activeSection + 1) }, true, true)
            "
            :disabled="loading"
          >
            Next
          </button>
          <loader-button
            v-else
            type="submit"
            class="button primary"
            :class="{ 'listing-btn-gtm': !isEditing, 'listing-btn-edit-gtm': isEditing }"
            :loading="loading"
            label="Submit"
          ></loader-button>
        </div>
      </div>
      <div class="w-100 float-left mt-4">
        <loader-button
          type="button"
          class="button clear mobile"
          label="Clear all"
          @click.native="clear"
        ></loader-button>
      </div>
    </div>
  </form>
</template>

<script>
import merge from "lodash/merge";
import cloneDeep from "lodash/cloneDeep";
import BaseModal from "../Base";
import InlineAuthPanel from "../../auth/InlineAuthPanel";
import Preview from "./partials/Preview";

let listingObject = {
  id: null,

  // Step 1
  sub_premises_number: "",
  street_number: "",
  street_address: "",
  suburb: "",
  state: "",
  postal_code: "",
  country: "",
  full_address: "",

  // Step 2
  location_type: "Private",
  property_type: null,
  description: "",
  space_types: [],
  gallery: {},
  extra_images: {},

  // Step 3
  availability_types: [],
  capacity: null,
  logistics: [],
  appraisal_requested: false,
  day_rate: null,
  currency: "AUD",
  agree_terms: false,

  // Step 4
  phone: null,

  // Meta Counts
  meta_counts: {
    space_types: {},
  },

  // Not used but needed in the backend
  name: null,
  is_black_label: false,
  pl_insurance_confirmed: false,
  price_on_application: 0,
};

export default {
  extends: BaseModal,
  props: {
    data: {
      type: Object,
      required: false,
    },
  },
  components: {
    InlineAuthPanel,
    Preview,
  },
  data() {
    return {
      uuid: null,
      loading: false,
      activeSection: 1,
      maxReachedSection: 1,
      listing: listingObject,
      initialized: false,
      errors: {},
      meta: {},
      manualAddress: false,
      maxImages: 20,
      noImagesWarning: false,
      noImagesWarningAlreadyShown: false,
      maxImagesWarningAlreadyShown: false,
    };
  },

  computed: {
    allowedPropertyTypes() {
      let options = [];

      for (let option of this.meta.property_types) {
        if (
          option["location_types"] &&
          option["location_types"][this.listing.location_type]
        ) {
          options.push(option);
        }
      }

      return options;
    },

    allowedSpaceTypes() {
      let options = [];

      for (let option of this.meta.space_types) {
        if (
          option["location_types"] &&
          option["location_types"][this.listing.location_type]
        ) {
          options.push(option);
        }
      }

      return options;
    },

    allowedLogistics() {
      let options = [];

      for (let category of this.meta.logistics) {
        for (let option of category.items) {
          if (
            option["location_types"] &&
            option["location_types"][this.listing.location_type]
          ) {
            options.push(option);
          }
        }
      }

      return options;
    },

    capacityOptions() {
      return this.meta.capacities;
    },

    propertyTypeLabel() {
      const propertyType = this.allowedPropertyTypes.find(
        (propertyType) => propertyType.id === this.listing.property_type
      );

      return propertyType ? propertyType.label : "";
    },

    /**
     * @todo Base this data on a Vuex store
     */
    countryOptions() {
      return [{ id: "AU", label: "Australia" }];
    },

    /**
     * Our form sections for our tab navigation.
     *
     * @return array
     */
    sections() {
      let items = [
        { id: 1, label: "Tell us about your property" },
        { id: 2, label: "Tell us about your property" },
        { id: 3, label: "Property Logistics" },
        { id: 4, label: "Review your property" },
      ];

      if (!this.authed) {
        items = [
          {
            id: "auth",
            label: "Log In",
          },
          ...items,
        ];

        this.activeSection = "auth";
      }

      return items;
    },

    showManualAddressFields() {
        return this.manualAddress || this.isEditing || this.maxReachedSection > 1;
    },

    stateOptions() {
      return this.meta.states;
    },

    areasAvialableOptions() {
      return this.meta.areas_available;
    },

    isEditing() {
      return !!(this.data && this.data.edit !== "undefined" && this.data.edit);
    },

    authed() {
      return !!this.$store.getters["user/account"];
    },
  },

  mounted() {
    this.listing = cloneDeep(listingObject);

    if (!this.isEditing) {
      axios.get("/api/account/listings/abandoned").then((response) => {
        if (response.data.maxReachedSection) {
          this.maxReachedSection = response.data.maxReachedSection;
          this.activeSection = response.data.maxReachedSection;
        }
        if (response.data.listing) {
          this.listing = merge(cloneDeep(listingObject), response.data.listing);
        }
        if (
          Array.isArray(this.listing.gallery) &&
          this.listing.gallery.length === 0
        ) {
          this.listing.gallery = {};
        }
      });
    }

    this.init();
  },

  methods: {
    init() {
      this.uuid = Date.now();
      this.loading = true;
      axios
        .get("/api/form-meta/listing")
        .then((response) => {
          this.meta = response.data;
          this.initialized = true;

          if (this.isEditing) {
            // Fetch attributes for prefill
            this.initialized = false;
            axios
              .get(`/api/account/listings/${this.data.edit}`)
              .then((response) => {
                this.listing = merge(cloneDeep(listingObject), response.data);
                this.initialized = true;
                this.setDefaultMetaCounts();
              })
              .catch(() => {})
              .then(() => (this.loading = false));
          }

          this.setDefaultMetaCounts();
        })
        .catch(() => {})
        .then(() => (this.loading = false));

        this.$store.dispatch("intercom/track", {
          type: "List with Us",
        });
        console.log('Intercom New: List with Us');
    },

    submitOrUpdateListing() {
      if (this.isEditing) {
        return this.updateListing();
      }

      this.submitListing();
    },

    openSection(which, scroll, validate) {
      let success = () => {
        this.activeSection = which.id;
        if (which.id > this.maxReachedSection) {
          this.maxReachedSection = which.id;
        }

        if (!this.isEditing) {
          axios.put("/api/account/listings/abandoned", {
            maxReachedSection: this.maxReachedSection,
            listing: this.listing,
          });
        }

        if (this.activeSection <= 3) {
          this.$store.dispatch("intercom/track", {
            type: "List with us - Step " + this.activeSection,
          });
          console.log('Intercom New: List with us - Step ' + this.activeSection);
        }

        if (scroll) {
          // Scroll to the clicked element
          let scrollTarget = this.$el.closest(".simplebar-content-wrapper");

          if (scrollTarget) {
            setTimeout(() => {
              scrollTarget.scrollTo({ top: 0, behavior: "smooth" });
            }, 50);
          }
        }
      };

      if (validate) {
        this.loading = true;
        this.errors = {};

        if (this.isEditing) {
          this.listing.id = this.data.edit;
        }

        this.listing.full_address = this.getFullAddress();

        axios
          .post(
            "/api/account/listings/validate/" + this.activeSection,
            this.listing
          )
          .then((response) => {
            success();
            this.$store.dispatch("intercom/track", {
              type: (this.isEditing ? "Edit" : "Add") + " Listing Step",
              meta: { step: which.id },
            });
          })
          .catch((error) => {
            this.displayValidationErrors(error);
          })
          .then(() => {
            this.loading = false;
          });
      } else {
        success();
      }
    },

    openSectionViaTab(which, scroll) {
      let active_idx = 0;
      let target_idx = null;

      for (let x = 0; x < this.sections.length; x++) {
        if (this.sections[x].id === this.activeSection) {
          active_idx = x;
        }

        if (this.sections[x].id === which.id) {
          target_idx = x;
        }
      }

      if (target_idx !== null && target_idx < active_idx) {
        this.openSection(which, scroll);
      }
    },

    fillAddress(result) {
      delete this.errors.country;
      delete this.errors.street_number;
      delete this.errors.street_address;
      delete this.errors.suburb;
      delete this.errors.state;
      delete this.errors.postal_code;
      this.listing.sub_premises_number = result.subpremise || null;
      this.listing.street_number = result.street_number || null;
      this.listing.street_address = result.route || null;
      this.listing.suburb = result.locality || null;
      this.listing.state = result.administrative_area_level_1 || null;
      this.listing.postal_code = result.postal_code || null;
      this.listing.country = "AU";
      this.manualAddress = true;
    },

    showListerTermsModal() {
      window.open("/pdf/lister-agreement.pdf", '_blank');
    },

    getFullAddress() {
      return (
        (this.listing.sub_premises_number
          ? this.listing.sub_premises_number + "/"
          : "") +
        (this.listing.street_number + " ") +
        (this.listing.street_address + ", ") +
        (this.listing.suburb + " ") +
        (this.listing.state + " ") +
        (this.listing.postal_code + " ")
      ).trim();
    },

    submitListing() {
      if (Object.keys(this.listing.gallery).length === 0) {
        if (this.noImagesWarningAlreadyShown === false) {
          this.noImagesWarning = true;
          this.noImagesWarningAlreadyShown = true;
          return;
        }
      }

      this.errors = {};
      this.loading = true;

      axios
        .post(
          "/api/account/listings/validate/" + this.activeSection,
          this.listing
        )
        .then(() => {
          if (!this.isEditing) {
            this.listing.name = `[tmp ${this.getFullAddress()}]`;
          }

          axios
            .post("/api/account/listings", this.listing)
            .then((response) => {
              this.$emit("confirm", this.listing);

              this.$eventBus.$emit("accountTableReload_listings-listing");
              this.$eventBus.$emit("update_listing_listing-item");

              this.$store
                .dispatch("modals/show", {
                  title: "Thank you for submitting your listing",
                  type: "listing-confirm",
                  narrow: true,
                })
                .catch(() => {});

              window.dataLayer.push({event: 'listing-submit'});

              this.$store.dispatch("intercom/track", {
                type: "Listing complete",
                meta: {
                  property_type: this.allowedPropertyTypes.find((o) => o.id === this.listing.property_type).label,
                  suburb: this.listing.suburb,
                }
              });
              console.log('Intercom New: Listing complete', {property_type: this.allowedPropertyTypes.find((o) => o.id === this.listing.property_type).label, suburb: this.listing.suburb});

              if (this.listing.phone) {
                this.$store.commit("user/setPhone", this.listing.phone);
              }

              this.listing = cloneDeep(listing);
            })
            .catch((error) => {
              this.$toasted.show(
                error.response.data,
                {
                  type: "error",
                }
              );
            })
            .then(() => (this.loading = false));
        })
        .catch((error) => {
          this.displayValidationErrors(error);
          this.loading = false;
        })
    },

    displayValidationErrors(error) {
      this.errors = error.response.data.errors || {};

      this.$nextTick(() => {
        let errorNodes = this.$el.querySelectorAll(".input-error");

        if (errorNodes[0]) {
          let tab = errorNodes[0].closest("[data-section]");

          if (tab) {
            this.openSection(
              { id: parseInt(tab.attributes["data-section"].value) },
              true
            );

            // Scroll to the clicked element
            let scrollTarget = this.$el.closest(".simplebar-content-wrapper");

            if (scrollTarget) {
              setTimeout(() => {
                scrollTarget.scrollTo({
                  top:
                    $(errorNodes[0]).offset().top -
                    $(scrollTarget).offset().top -
                    100,
                  behavior: "smooth",
                });
              }, 50);
            }
          }
        }
      });
    },

    updateListing() {
      this.loading = true;
      axios
        .put(`/api/account/listings/${this.data.edit}`, this.listing)
        .then(() => {
          this.$emit("confirm", this.listing);

          this.$eventBus.$emit("accountTableReload_listings-listing");
          this.$eventBus.$emit("update_listing_listing-item");

          this.$store
            .dispatch("modals/show", {
              title: "Your Listing has been updated",
              type: "listing-confirm",
              narrow: true,
            })
            .then(() => {})
            .catch(() => {});

          this.$store.dispatch("intercom/track", {
            type: "Edit Listing",
          });
          console.log('Intercom New: Edit Listing');
        })
        .catch((error) => {
          this.displayValidationErrors(error);
        })
        .then(() => (this.loading = false));
    },

    setDefaultMetaCounts() {
      this.$nextTick(() => {
        const set = (item, prop) => {
          if (!item.is_countable) {
            return;
          }
          if (
            typeof this.listing.meta_counts[prop] === "undefined" ||
            typeof this.listing.meta_counts[prop][item.id] === "undefined" ||
            !this.listing.meta_counts[prop][item.id]
          ) {
            if (this.listing.meta_counts[prop] === "undefined") {
              this.listing.meta_counts[prop] = {};
            }
            this.listing.meta_counts[prop][item.id] = 0;
          }
        };

        ["space_types"].forEach((prop) => {
          this.meta[prop].forEach((meta) => {
            if (typeof meta.items !== "undefined") {
              meta.items.forEach((item) => {
                set(item, prop);
              });
              return;
            }
            set(meta, prop);
          });
        });
      });
    },

    isNowAuthed() {
      if (this.$store.getters["user/isHirer"]) {
        this.$emit("cancel");
      }
      this.activeSection = 1;
    },

    decrement(option_id) {
      // Decrement space types meta counts
      const metaCountsSpaceTypes = cloneDeep(
        this.listing.meta_counts.space_types
      );
      if (metaCountsSpaceTypes[option_id] >= 1) {
        metaCountsSpaceTypes[option_id]--;
      }
      this.listing.meta_counts.space_types = metaCountsSpaceTypes;

      // Remove element from space types array
      if (this.listing.meta_counts.space_types[option_id] == 0) {
        if (this.listing.space_types.indexOf(option_id) !== -1) {
          const index = this.listing.space_types.indexOf(option_id);
          this.listing.space_types.splice(index, 1);
        }
      }
    },

    increment(option_id) {
      // Increment space types meta counts
      const metaCountsSpaceTypes = cloneDeep(
        this.listing.meta_counts.space_types
      );
      metaCountsSpaceTypes[option_id]++;
      this.listing.meta_counts.space_types = metaCountsSpaceTypes;

      // Add element to space types array
      if (this.listing.space_types.indexOf(option_id) === -1) {
        this.listing.space_types.push(option_id);
      }
    },

    clear() {
      this.$store
        .dispatch("modals/show", {
          title: "Clear listing?",
          cancelLabel: "No",
          confirmLabel: "Yes",
          confirmMessage: `Are you sure you want to clear this listing?`,
          secondaryStyling: true
        })
        .then(() => {
          axios
            .delete("api/account/listings/abandoned")
            .then(() => {
              this.listing = cloneDeep(listingObject);
              this.activeSection = 1;
              this.maxReachedSection = 1;
              this.manualAddress = false;
              this.init();
            })
            .catch(() => {
              this.$toasted.show('Your listing could not be deleted, please try again.', {
                type: 'error'
              })
            })
        })
    },

    isReadyToSubmitHandler(value) {
      this.loading = !value;
    },
  },

  created() {
    this.$store.dispatch("intercom/track", {
      type: (this.isEditing ? "Edit" : "Add") + " Listing Opened",
    });
  },
  watch: {
    "listing.gallery": {
      handler () {
        if(Object.keys(this.listing.gallery).length >= this.maxImages && !this.maxImagesWarningAlreadyShown) {
          this.$toasted.show(
            "You have reached the upload limit of " + this.maxImages + " images. Additional images have been removed from upload. To add more, please edit your listing after it has been submitted for review.",
            {
              type: "error",
              duration: 30000, 
              action : {
                  text : '✕', 
                  onClick : (e, toastObject) => {
                      toastObject.goAway(0);
                  }
              },
            }
          );
          this.maxImagesWarningAlreadyShown = true;
        } else if(Object.keys(this.listing.gallery).length < this.maxImages) {
          this.maxImagesWarningAlreadyShown = false;
        }
      },
      deep: true
    },
    "listing.extra_images": {
      handler () {
        if(Object.keys(this.listing.extra_images).length >= this.maxImages && !this.maxImagesWarningAlreadyShown) {
          this.$toasted.show(
            "You have reached the upload limit of " + this.maxImages + " images. Additional images have been removed from upload. To add more, please edit your listing after it has been submitted for review.",
            {
              type: "error",
              duration: 30000, 
              action : {
                  text : '✕', 
                  onClick : (e, toastObject) => {
                      toastObject.goAway(0);
                  }
              },
            }
          );
          this.maxImagesWarningAlreadyShown = true;
        } else if(Object.keys(this.listing.extra_images).length < this.maxImages) {
          this.maxImagesWarningAlreadyShown = false;
        }
      },
      deep: true
    }
  },
};
</script>

<style lang="scss" scoped>
</style>>
